/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *\

  Random formatted strings

  Author Alex Lowe

\* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

import { inBrowser, baseURL, mainMode, appMode } from "@kit/utils/EnvironmentHelper"

const MONTHS_SHORT_1_IDX = [null, "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
const MONTHS_LONG_1_IDX =  [null, "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"]


export const listDelimiterAPStyle = (which, len) => {

  //no delimiters of only one single element
  if(len == 1) {
    return false
  } 
  
  //an and in-between if two elements
  else 
  if(len == 2) {
    if(which == 0) {
      return " and "
    }
  } else 

  //e.g. lenth of 4.
  //0    1     2          3
  //pear, apple, grapes and bananas
  if(len > 2) {
    if(which == len - 2) {
      return " and "
    } else 
    if(which < len - 2) {
      return ", "
    }
  }

  return false
}


/**
 * Make sure that a string ends with an end-character
 * 
 *
 * @param {String, required} sourceString. The source string 
 * @param {String, required} endChar. The ending character to enforce 
 * @returns {String} a new string, or the original string if unaltered.
 * 
 */
export const endChar = (sourceString, endChar, trim) => {
  if(!sourceString) {
    return endChar
  }

  const s = trim ? sourceString.trim() : sourceString
  const last = s[s.length-1];
  if(last == endChar) {
    return s
  } else {
    return `${s}${endChar}`
  }
}


/**
 * 
 * @param {String, required} classes. the default classes for a particular component element
 * @param {Object, optional} themeSlot. the object within the theme:
 *    { append:"stuff" }
 *    or
 *    { override:"stuff" } 
 * 
 *    or 
 *     "stuff"
 *  
 * In the last case, it will be assumed that we're appending and not overriding.
 * 
 * @returns a string with the default, default + classes or classes.
 * 
 */
export const mergeClassesTheme = (classes, themeSlot) => {
  if(!themeSlot) {
    return classes
  }

  if(themeSlot.constructor == String) {
    return `${classes} ${themeSlot}`
  } else 
  if(themeSlot.append) {
    return `${classes} ${themeSlot.append}`
  } else 
  if(themeSlot.override) {
    return themeSlot.override
  }

  return classes
}

export const mergeStylesTheme = (style, themeSlot) => {
  if(!themeSlot) {
    return style
  }

  if(themeSlot.constructor == String) {
    return `${endChar(style,';', true)} ${themeSlot}`
  } else 
  if(themeSlot.append) {
    return `${endChar(style,';', true)} ${themeSlot.append}`
  } else 
  if(themeSlot.override) {
    return themeSlot.override
  }

  return style
}

/**
 * @returns the current year.
 *  
 */
export const getCurrentYear = () => {
  return new Date().getFullYear()
}


/**
 * Format the date-object as mm/dd/yyyy with zero-padding
 * Also an alternate one
 * 
 * @param {Date} _date. Optional. If absent, then it will just use now 
 * @returns {String} formatted strind
 * 
 */
export const formatDateAsMMDDYYYY = (_date) => {
  const date = _date || new Date()
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
}
export const formatDateAsMMDDYYYYAlt = (_date) => {
  const date = _date || new Date()
  const str = date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  return str
}

/**
 * Format a date-object as military-time
 * 
 * @param {Date} _date. Optional. If absent, then it will just use now 
 * @returns {String} formatted string
 * 
 */
export const formatDateAsMilitaryTime = (_date) => {
  const date = _date || new Date()
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

export const splitMilitaryTime = (mTime, asNumber) => {

  if(!mTime) {
    return null
  }

  const split = mTime.split(":")

  if(!split || split.length == 0) {
    return null
  }

  const h = parseInt(split[0], 10)
  const m = parseInt(split[1], 10) 
  const s = split.length == 2 ? parseInt(split[2], 10) : 0 

  return { h, m, s }
}

//returns 1 if t1 > t2, 
//returns -1 if t1 < t2 
//returns 0 if t1 == t2
export const compareTwoMilitaryTimes = (t1, t2) => {

  const s1 = splitMilitaryTime(t1)
  const s2 = splitMilitaryTime(t2)
  const s1_gt_s2 = 1
  const s1_lt_s2 = -1 
  const s1_eq_s2 = 0

  //compare hours
  if(s1.h > s2.h) {
    return s1_gt_s2
  } else
  if(s1.h < s2.h) {
    return s1_lt_s2
  }

  //hours must be the same.
  //now compare min.
  if(s1.m > s2.m) {
    return s1_gt_s2
  } else 
  if(s1.m < s2.m) {
    return s1_lt_s2
  }

  //minutes must be the same 
  //now compare sec.
  if(s1.s > s2.s) {
    return s1_gt_s2
  } else 
  if(s1.s < s2.s) {
    return s1_lt_s2
  }

  //If we got this far, then they must be equal.
  return s1_eq_s2
}

/**
 * 
 * @param {*} cDate: a string like 0000-00-00 00:00:00 (from pods date value)
 * @param {*} defVal: the default value in case of an empty date.
 * @returns a nice date like: 3/20/2024
 * 
 */
export const niceDate = (cDate, defVal) => {
  if( cDate && cDate.indexOf('0000-00-00') == -1) {
    var d = new Date(cDate)
    return (d.getMonth()+1)+"/"+d.getDate()+"/"+d.getFullYear()  
  } else {
    return defVal
  }
}


/** 
 * Returns a string yyyy-mm-dd from a javascript date object.
 * 
 * @param {Date, required} dateObj
 * 
 */

//export const dateToYMD = (dateObj) => {
//  return dateObj.toISOString().split('T')[0]
//}
//https://stackoverflow.com/questions/23593052/format-javascript-date-as-yyyy-mm-dd
//BE CAREFUL with this method as it first converts to the date to UTC. If you are in a + timezone and your time portion is early in the day, then it could roll-back a day. Alternatively, 
//if you're in a - timezone and your time portion is late in the day, then it could roll forward a day. 

export const dateToYMD = (dateObj) => {
  var d = dateObj,
      month = '' + (d.getMonth() + 1),
      day = '' + d.getDate(),
      year = d.getFullYear();

  if (month.length < 2) 
      month = '0' + month;
  if (day.length < 2) 
      day = '0' + day;

  return [year, month, day].join('-');
}

export const ymdToDate = (yyyymmdd) => {
  const split = yyyymmdd.split("-")
  const dateObject = new Date(parseInt(split[0]), parseInt(split[1])-1, parseInt(split[2]));
  return dateObject
}

export const isYMDBeforeOrEqualToday = (yyyymmdd) => {
  const todayDate = new Date()
  const testDate = ymdToDate(yyyymmdd)
  if(testDate <= todayDate) {
    return true
  } else {
    return false
  }
}


//Get today's date in YYYY-MM-DD format.
//Thank you, https://stackoverflow.com/questions/23593052/format-javascript-date-as-yyyy-mm-dd
export const todayYYYYMMDD = () => {
  const todayDate = new Date()
  const offset = todayDate.getTimezoneOffset()
  const newDate = new Date(todayDate.getTime() - (offset*60*1000))
  return newDate.toISOString().split('T')[0]
}


//get tomorrow
//http://stackoverflow.com/questions/9989382/how-can-i-add-1-day-to-current-date
export const getTomorrowDate = () =>  {
  const date = new Date();

  // add a day
  date.setDate(date.getDate() + 1);

  return date
}


export const ymdToNiceDateString = (yyyymmdd) => {
  if(!yyyymmdd) {
    return ""
  }

  const d = typeof yyyymmdd == "object" ? yyyymmdd.toDateString() : new Date(yyyymmdd+" 00:00").toDateString()

  //Thu Jun 13 2024 -> [Thu, Jun, 14, 2024]
  const s = d.split(" ")
  let month = s[1]
  if(month == "Jul") {
    month = 'July'
  } else 
  if(month == "Jun") {
    month = "June"
  }

  return `${s[0]} ${month} ${s[2]}, ${s[3]}`  
}

//yyyymmdd
export const ymdToNiceDateString2 = (yyyymmdd, monthShort) => {
  if(!yyyymmdd) {
    return ""
  }
  const split = yyyymmdd.split("-")
  const months = monthShort ? MONTHS_SHORT_1_IDX : MONTHS_LONG_1_IDX
  const m = parseInt(split[1])
  return `${months[m]} ${split[2]}, ${split[0]}`  
}



export const ymdToNiceDateString3 = (yyyymmdd) => {
  if(!yyyymmdd) {
    return ""
  }
  const split = yyyymmdd.split("-")
  return `${split[1]}/${split[2]}/${split[0]}`  
}

//yyyymmdd
//also yyyymmdd hh:mm:ss
//Will return date and time as separate properties
export const ymdToNiceDateString4 = (yyyymmdd, monthShort) => {
  if(!yyyymmdd) {
    return ""
  }
  const split = yyyymmdd.split("-")
  const months = monthShort ? MONTHS_SHORT_1_IDX : MONTHS_LONG_1_IDX
  const m = parseInt(split[1])

  const middlePart = split[2]
  let time = null 

  //e.g. a day of the month and then a military time
  //09 13:00:00
  if(middlePart.indexOf(" ") > -1) {
    const miltime = middlePart.split(" ")[1]
    time = militaryToStandardTime(miltime)
  }

  const day = parseInt(split[2], 10)
  const month = months[m]
  const year = split[0]

  const dayOfWeek = new Date(parseInt(year), m - 1, day).toLocaleDateString('en-US', { weekday: 'long' })
  const date = `${month} ${day}, ${year}`
  const date2 = `${dayOfWeek}, ${date}`

  return {date, date2, month, day, year, time, dayOfWeek }  
}

// This will take a floating number out of a dirty string.
// "$666,333 Yee ha!! ,, .35!!" => 666333.35
// Handy for form validation, especially currency.
//
export const stripFloat = (dirty, toNum) => {
  if(!yyyymmdd) {
    return toNum ? NaN : ""
  }
  var s = dirty.replace(/[^\-0-9\.]+/g,"");
  if(s == '') {
    return toNum ? NaN : null;
  } 
  return toNum ? Number(s) : s;
}

// This will take an integer out of a dirty string.
// "$666,333 Yee ha!! ,, .35!!" => 666333.35
// Handy for form validation, especially phone numbers
//
export const stripInt = (dirty, toInt) => {
  if(!yyyymmdd) {
    return toNum ? NaN : ""
  }
  var s = dirty.replace(/[^0-9]+/g,"");
  if(s == '') {
    return toInt ? NaN : null;
  } 
  return toInt ? parseInt(s) : s;
}


//If the raw string can be turned into an integer and then grouped
//into a phone number, then this will return the formatted phone number string.
//else, it will return null.
export const phoneNumber = (rawString) => {

  if(!rawString) {
    return null
  }

  const cleanNumString = stripInt(rawString)

  if(!cleanNumString) {
    return null
  }

  const c = cleanNumString

  if(c.length == 10) {
    return '('+c[0]+c[1]+c[2]+') '+c[3]+c[4]+c[5]+'-'+c[6]+c[7]+c[8]+c[9]
  }

  return null
}


/**
 * @param {String} val the candidate email address 
 * @returns {boolean} true if email, false if otherwise
 * 
 */
export const emailAddressValid = (val) => { 
  if(/^[a-z0-9][a-z0-9-_\.]+@([a-z]|[a-z0-9]?[a-z0-9-]+[a-z0-9])\.[a-z0-9]{2,10}(?:\.[a-z]{2,10})?$/.test(val)) {
    return true
  }
  return false
}



/**
 * Returns the path for a url. Everything after the protocol and domain
 * Take
 *    https://something.com/some-place/image.png
 * or 
 *    @images/some-place/image.png
 * 
 * And this will return 
 *    /some-place/image.png
 * 
 * @param {string} url. required. 
 * @returns the path
 */
export const pathname = (url) => {

  //Due to the fact that this is used in prefetch rendering situations, it's disasterous for this 
  //to have an unhandled error if the url doesn't exist due to a malformed data-service.
  //So in the browser situation we throw an error whereas on the server we just return an empty string here.
  if(!url) {
    if(inBrowser) {
      throw new Error("pathname: missing url")
    } else {
      return ""
    }
  }

  if(url.indexOf("@images") == 0) {
    return url.replace("@images","/assets")
  } else 
  if(url.indexOf("/") == 0) {
    return url
  }

  //trim
  url = url.trim()

  //the http or https
  let protocolLen = 4
  if(url.indexOf("https") > -1) {
    protocolLen = 5
  }
  //the ://
  protocolLen += 3
  
  //localhost or www.something.io
  const noProtocol = url.substr(protocolLen)

  //everything with letters dots dashes and underscores is the subdomain, domain
  //the dot and the top-level-domain. just replace that with the empty string and voila.
  const path = noProtocol.replace(/^[a-zA-Z0-9\-_\.]+/, "")

  //if its empty, then just a slash
  if(path == "") {
    return "/"
  } else {
    return path
  }
}


// Takes a number of messy formats and outputs the correct path to the cms-static-assets 
//
// /cms-static-assets/some-file
// /some-file
// https://localhost/cms-static-assets/some-file
// https://something.com/cms-static-assets/some-file
// localhost/cms-service/wp-content/uploads/2023/03/pexels-burak-the-weekender-186461-resized.png 
//
// Sometimes the outputs from wordpress can be messy.
//
// Turn it into:
// /cms-static-assets/some-file
//
// For images, we can add in an optional size.
// The options are 150, 300, 768, 1024, 1536, 2048  
// See README 968583 for details.
//
/*
export const cmsasset = (url, size) => {

  //Due to the fact that this is used in prefetch rendering situations, it's disasterous for this 
  //to have an unhandled error if the url doesn't exist due to a malformed data-service.
  //So in the browser situation we throw an error whereas on the server we just return an empty string here.
  if(!url) {
    if(inBrowser) {
      throw new Error("cmsasset: missing url")
    } else {
      return ""
    }
  }

  //trim
  url = url.trim()

  let cmsPath = ""

  if(url.indexOf("@images") == 0) {
    throw new Error("cmsasset is not supposed to be used with @images. use pathname")
  } else 
  if(url.indexOf("/cms-static-assets") == 0) {
    cmsPath = url
  } else
  if(url.indexOf("/") == 0) {
    cmsPath = `${baseURL}/cms-static-assets${url}`
  } else {

    //the http or https
    let protocolLen = 4
    if(url.indexOf("https") > -1) {
      protocolLen = 5
    }
    //the ://
    protocolLen += 3

    const noProtocol = url.substr(protocolLen)
    //   localhost/cms-service/wp-content/uploads/2023/03/pexels-burak-the-weekender-186461-resized.png

    const protocol = url.substr(0, protocolLen)
    //   https://

    const m = noProtocol.match(/^[a-zA-Z0-9\-_\.]+/)
    const domain = m[0]
    //   localhost
    
    //everything with letters dots dashes and underscores is the subdomain, domain
    //the dot and the top-level-domain. just replace that with the empty string and voila.
    const barePath = noProtocol.replace(/^[a-zA-Z0-9\-_\.]+/, "")
    //   /cms-service/wp-content/uploads/2023/03/pexels-burak-the-weekender-186461-resized.png
    
    const staticAssets = barePath.replace(/^(.*?)uploads/,"/cms-static-assets")
    //   /cms-static-assets/2023/03/pexels-burak-the-weekender-186461-resized.png
    //cmsPath = `${baseURL}${staticAssets}`
    cmsPath = staticAssets

    //See README: 968583
    //We have to take off -scaled.jpg and swap it out for -<the-size>.jpg
    //The options are: 150, 300, 1024, 864, 1536, 2048  
    if(size) {

      const lastIdxOfScaled = cmsPath.lastIndexOf("-scaled.")

      if(lastIdxOfScaled > -1) {

        //  0  1  2  3  4  5  6  7  8  9  10 11 12 13 14 15 16 17 18 19 20 21
        //  m  y  -  c  o  o  l  -  i  m  g  -  s  c  a  l  e  d  .  j  p  g

        //lastIndexOfScaled = 11.
        //slice( inclusiveStart, exclusiveEnd )
        //slice( inclusiveStart )

        //                           0, 11  -- grabs all of 0 - 10
        const split0 = cmsPath.slice(0, lastIdxOfScaled)

        //                           11 + 8 (length of -scaled.) = 19. -- grabs 19 - end. so "jpg"
        const split1 = cmsPath.slice(lastIdxOfScaled + 8)

        //If the extension is something weird it means that the 
        //-scaled. was in the middle. Just return the cmsPath if that's the case.
        const ext = split1
        if(ext.length > 4) {
          return cmsPath
        } 
        
        //else concatenate the new cmsPath.
        else {
          cmsPath = split0+ "-" + size + "." + split1
        }

      } else {

        const lastIdxOfDot = cmsPath.lastIndexOf(".")

        //no idea why but just return the cmsPath
        if(lastIdxOfDot == -1) {
          return cmsPath
        } 
        
        //Else, it has a dot on the end, so we're going to 
        //perform the same kind of annoying boundary mechanics as we did above.
        else {
          const split0 = cmsPath.slice(0, lastIdxOfDot)
          const split1 = cmsPath.slice(lastIdxOfDot+1)
          cmsPath = split0+ "-" + size + "." + split1
        }
    
      }

    }

  }

  return cmsPath
}
*/


/**
 * 
 * @param {String } url 
 * @returns { path, ext }
 * 
 * For a url like this 
 * https://something.com/uploads/my-cool-image.png this will return
 * { path: "/cms-static-assets/my-cool-image", ext:"png"}
 * And this makes it easy to concatenate sizes and srcsets
 */
export const cmsurl = (url, scaledEdgecase) => {

  //Due to the fact that this is used in prefetch rendering situations, it's disasterous for this 
  //to have an unhandled error if the url doesn't exist due to a malformed data-service.
  //So in the browser situation we throw an error whereas on the server we just return an empty string here.
  if(!url) {
    //if(inBrowser) {
    //  throw new Error("cmsasset: missing url")
    //} else {
      return ""
    //}
  }

  //trim
  url = url.trim()

  let cmsPath = ""

  if(url.indexOf("@images") == 0) {
    throw new Error("cmsasset is not supposed to be used with @images. use pathname")
  } 

  if(url.indexOf("/cms-static-assets") == 0) {
    cmsPath = url
  } else
  if(url.indexOf("/") == 0) {
    cmsPath = `${baseURL}/cms-static-assets${url}`
  } else {
    //the http or https
    let protocolLen = 4
    if(url.indexOf("https") > -1) {
      protocolLen = 5
    }
    //the ://
    protocolLen += 3

    const noProtocol = url.substr(protocolLen)
    //   localhost/cms-service/wp-content/uploads/2023/03/pexels-burak-the-weekender-186461-resized.png
    
    //everything with letters dots dashes and underscores is the subdomain, domain
    //the dot and the top-level-domain. just replace that with the empty string and voila.
    const barePath = noProtocol.replace(/^[a-zA-Z0-9\-_\.]+/, "")
    //   /cms-service/wp-content/uploads/2023/03/pexels-burak-the-weekender-186461-resized.png
    
    const staticAssets = barePath.replace(/^(.*?)uploads/,"/cms-static-assets")
    //   /cms-static-assets/2023/03/pexels-burak-the-weekender-186461-resized.png

    //cmsPath = `${baseURL}${staticAssets}`
    cmsPath = staticAssets

  }

  //There's this annoying cases of some wordpress image have this "scaled"
  //suffix on the end of them and we have to deal with that.
  const lastIdxOfScaled = cmsPath.lastIndexOf("-scaled.")

  let path = ''
  let ext = ''

  //We may want to treat the "-scaled." images specially.
  if(lastIdxOfScaled > -1 && scaledEdgecase) {

    //  0  1  2  3  4  5  6  7  8  9  10 11 12 13 14 15 16 17 18 19 20 21
    //  m  y  -  c  o  o  l  -  i  m  g  -  s  c  a  l  e  d  .  j  p  g

    //                           0, 11  -- grabs all of 0 - 10
    const split0 = cmsPath.slice(0, lastIdxOfScaled)

    //                           11 + 8 (length of -scaled.) = 19. -- grabs 19 - end. so "jpg"
    const split1 = cmsPath.slice(lastIdxOfScaled + 8)

    //cmsPath = split0+ "-" + size + "." + split1
    path = split0 
    ext = split1
    
    

  } else {

    const lastIdxOfDot = cmsPath.lastIndexOf(".")

    //we're going to  perform the same kind of annoying boundary mechanics as we did above.
    
    const split0 = cmsPath.slice(0, lastIdxOfDot)
    const split1 = cmsPath.slice(lastIdxOfDot+1)
    //cmsPath = split0+ "-" + size + "." + split1
    
    path = split0 
    ext = split1

  }

  return{ path, ext }
}

// Takes a number of messy formats and outputs the correct path to the cms-static-assets 
//
// /cms-static-assets/some-file
// /some-file
// https://localhost/cms-static-assets/some-file
// https://something.com/cms-static-assets/some-file
// localhost/cms-service/wp-content/uploads/2023/03/pexels-burak-the-weekender-186461-resized.png 
//
// Sometimes the outputs from wordpress can be messy.
//
// Turn it into:
// /cms-static-assets/some-file
//
// For images, we can add in an optional size.
// The options are 150, 300, 768, 1024, 1536, 2048  
// See README 968583 for details.
//
export const cmsasset = (url, size, scaledEdgecase) => {

  const { path, ext } = cmsurl(url, scaledEdgecase)

  const cmsPath = size ? `${path}-${size}.${ext}` : `${path}.${ext}`

  return cmsPath
}



/**
 * @param {String} name the name of the parameter to retrieve from the window url, provided it's the google custom search 
 *  string like this: https://localhost/#gsc.tab=0&gsc.sort=&gsc.q=healthy%20dinner .
 *  In this case, the names of the available parameters are "sort" and "q". We ignore "gsc." and the first one, "tab" is ignored.
 *  
 * @returns {String} the value for the parameter
 * 
 */
export const getGCSParameterByName = (name) => {
  const fullURL = inBrowser ? window.location.href : ""
  const split1 = fullURL.split("#gsc.tab=0")
  const split2 = split1.length == 2 ? split1[1] : ""
  const split3 = split2.split("&gsc.")

  //first one is blank, hence i=1
  for(let i=1; i<split3.length; i++) {
    const item = split3[i]
    const itemSplit = item.split("=")
    if(itemSplit.length == 2) {
      const key = itemSplit[0]
      const val = decodeURIComponent(itemSplit[1])
      if(key == name) {
        return val
      }
    }
  }
  return null 
}

export const slugify = (rawString) => {
  return rawString
    .toLowerCase() // Convert to lowercase
    .replace(/[^a-z0-9 -]/g, '') // Remove invalid chars
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-'); // Collapse multiple hyphens
}

 
export const numberWithCommas = (x) => {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export const formatUSDCurrency = (rawNum) => {
  const toFixed = rawNum.toFixed(2)
  const withCommas = toFixed.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `$${withCommas}`
}


export const militaryToStandardTime = (militaryTime) => {
  // Assuming militaryTime is a string in "HH:MM" or "HH:MM:SS" format
  const parts = militaryTime.split(':');
  let hours = parseInt(parts[0]);
  const minutes = parts[1];
  const seconds = parts[2] ? `:${parts[2]}` : ''; // Include seconds if present

  let period = "AM";

  if (hours >= 12) {
    period = "PM";
    if (hours > 12) {
      hours -= 12;
    }
  } else if (hours === 0) {
    hours = 12; // Midnight (00:XX) becomes 12:XX AM
  }

  // Pad hours with leading zero if less than 10 (optional, but common for consistency)
  //const formattedHours = hours < 10 && hours !== 0 ? `0${hours}` : hours;
  //return `${formattedHours}:${minutes}${seconds} ${period}`;
  return `${hours}:${minutes} ${period}`;
}

// // Examples:
// console.log(convertMilitaryToStandardTime("14:30"));    // Output: 2:30 PM
// console.log(convertMilitaryToStandardTime("00:00"));    // Output: 12:00 AM
// console.log(convertMilitaryToStandardTime("12:00"));    // Output: 12:00 PM
// console.log(convertMilitaryToStandardTime("09:15:45")); // Output: 09:15:45 AM
// console.log(convertMilitaryToStandardTime("23:59"));    // Output: 11:59 PM

//generate a random string
export const randomAlphaNumString = (length) => {
  return Math.random().toString(36).substring(2, 2 + length);
}